import React, { useEffect, useMemo, useState } from "react";
import { styled, formGap, sectionalMargin } from "@iventis/styles";
import { Body2, Header4 } from "@iventis/styles/src/components/texts";
import { Content } from "@iventis/translations/content/typed-content";
import { Button } from "@mui/material";
import { LoadingComponent, ConfirmationDialogComponent } from "@iventis/components";
import { createUserNodeComponent, createPeopleNodeTree, createPersonNode, AdminTreeNodeType, AdminPersonNode, AddPeopleModal, PeopleNodeTree } from "@iventis/people";
import { TreeBrowserComponent } from "@iventis/tree-browser";
import { getProjectAdmins, removeProjectAdmin, setProjectAdmin } from "@iventis/permissions/src/permissions";
import { User } from "@iventis/domain-model/model/user";
import { toast } from "@iventis/toasts/src/toast";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { IventisErrorBoundaryWithToast } from "@iventis/error-boundaries";
import { useDispatch } from "react-redux";
import { authSlice } from "@iventis/permissions/src/auth.slice";
import { getPeople, getTeams, postNewUserAndGroups } from "@iventis/permissions/src/people-api-functions";
import { ProjectUser } from "@iventis/domain-model/model/projectUser";

enum AddAdminFormType {
    CLOSED = "CLOSED",
    SELECTION = "SELECTION",
    SAVING = "SAVING",
}

export const ProjectRolePermissions: React.FC<{ projectId: string; userId: string }> = ({ projectId, userId }) => {
    const translate = useIventisTranslate();
    const dispatch = useDispatch();
    const [nodeTree, setNodeTree] = useState<PeopleNodeTree>(undefined);
    const [addAdminForm, setAddAdminForm] = useState<AddAdminFormType>(AddAdminFormType.CLOSED);
    const [removeUserId, setRemoveUserId] = useState<string>(undefined); // not null when removing a user. confirmation dialog will show.
    useEffect(() => {
        // Get Project admins list.
        getProjectAdmins(projectId).then((users) => {
            setNodeTree(createPeopleNodeTree(users.map((u) => u as ProjectUser)));
        });
    }, [projectId]);

    const removeAdmin = (userId: string) => {
        removeProjectAdmin(userId, projectId).then(() =>
            toast.success({
                title: translate(Content.common.success),
                message: translate(Content.settings3.projectRolePermissions.removeAdminToast),
            })
        );
        setNodeTree((n) => ({ ...n, childNodes: n.childNodes.filter((userNode) => userNode.id !== userId) }));
    };

    const addAdmins = (users: User[]) => {
        setAddAdminForm(AddAdminFormType.SAVING);
        if (users.length === 0) {
            setAddAdminForm(AddAdminFormType.CLOSED);
            return;
        }
        const promises = users.map((user) => setProjectAdmin(user.id, projectId));
        Promise.all(promises)
            .then(() => {
                setAddAdminForm(AddAdminFormType.CLOSED);
                const newAdminNodes = users.map((user) => createPersonNode(user as ProjectUser, nodeTree.id));
                setNodeTree((n) => ({
                    ...n,
                    childNodes: [...n.childNodes, ...newAdminNodes].sort((childA, childB) => childA.name.toLowerCase().localeCompare(childB.name.toLowerCase())),
                }));
                toast.success({
                    title: translate(Content.common.success),
                    message: translate(Content.settings3.projectRolePermissions.addAdminsToast),
                });
            })
            .catch(() => setAddAdminForm(AddAdminFormType.SELECTION));
    };

    const peopleNodeComponents = useMemo(
        () => ({
            [AdminTreeNodeType.Root]: undefined,
            [AdminTreeNodeType.User]: createUserNodeComponent<AdminPersonNode>({
                showCheckBoxColumn: false,
                showOverflowMenuColumn: true,
                getOverflowOptions: ({ person }) => [
                    {
                        text: translate(Content.settings3.projectRolePermissions.removeAdmin),
                        action: () => setRemoveUserId(person.id),
                        disabled: nodeTree !== undefined && nodeTree.childNodes?.length <= 1,
                    },
                ],
            }),
        }),
        [nodeTree]
    );

    return (
        <>
            <StyledContainer>
                <ProjectDetailsHeader>
                    <ProjectAdminRow>
                        <div>
                            <Header4>{translate(Content.settings3.projectRolePermissions.projectAdministratorsTitle)}</Header4>
                            <Body2>{translate(Content.settings3.projectRolePermissions.projectAdminRoleDescription)}</Body2>
                        </div>
                        <Button variant="text" size="large" color="primary" onClick={() => setAddAdminForm(AddAdminFormType.SELECTION)}>
                            {translate(Content.settings3.projectRolePermissions.addAdmin)}
                        </Button>
                    </ProjectAdminRow>
                </ProjectDetailsHeader>
                <IventisErrorBoundaryWithToast toast={toast}>
                    {nodeTree && (
                        <TreeBrowserContainer>
                            <TreeBrowserComponent className="tree-browser" node={nodeTree} displayRootNode={false} components={peopleNodeComponents} openNodes={[]} />
                        </TreeBrowserContainer>
                    )}
                    {!nodeTree && <LoadingComponent />}
                </IventisErrorBoundaryWithToast>
            </StyledContainer>
            <AddPeopleModal
                isSubmitting={addAdminForm === AddAdminFormType.SAVING}
                open={addAdminForm !== AddAdminFormType.CLOSED}
                title={translate(Content.settings3.projectRolePermissions.addProjectAdmin)}
                addPeople={(users: User[]) => addAdmins(users)}
                projectId={projectId}
                confirmButtonText={translate(Content.settings3.projectRolePermissions.addAdmin)}
                unavailablePersonIds={nodeTree !== undefined ? nodeTree.childNodes.map((user) => user.id) : []}
                apiCallbacks={{
                    getPeople,
                    getTeams,
                    postNewUserAndGroups: postNewUserAndGroups((data) => dispatch(authSlice.actions.setUsage({ usage: "adjustUsers", adjustment: data.length }))),
                }}
                toast={toast}
                translate={translate}
            />
            <ConfirmationDialogComponent
                show={removeUserId !== undefined}
                title={translate(Content.settings3.projectRolePermissions.removeAdmin)}
                message={
                    userId === removeUserId
                        ? translate(Content.settings3.projectRolePermissions.removeYourselfAdminConfirm)
                        : translate(Content.settings3.projectRolePermissions.removeAdminConfirm)
                }
                confirmText={translate(Content.common.buttons.confirm)}
                cancelText={translate(Content.common.buttons.close)}
                isSubmitting={false}
                handleCancel={() => {
                    setRemoveUserId(undefined);
                }}
                handleConfirm={async () => {
                    removeAdmin(removeUserId);
                    // If they remove themselves, nagivate away from project admin page.
                    if (removeUserId === userId) {
                        window.location.replace("/"); // useHistory hook did not work because we have nested routers and would only use the child router.
                    }
                    setRemoveUserId(undefined);
                }}
            />
        </>
    );
};

const ProjectAdminRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`;

const ProjectDetailsHeader = styled.div`
    h5 {
        display: flex;
        justify-content: center;
        flex-direction: column;
    }
    p {
        margin-bottom: ${formGap};
    }
    margin-bottom: ${sectionalMargin};
`;

const StyledContainer = styled.div`
    flex-grow: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    padding: ${sectionalMargin};
`;

const TreeBrowserContainer = styled.div`
    display: flex;
    flex-direction: column;
    overflow-y: auto;
`;
